import React, { useEffect, useRef, useState } from 'react';

export const CLOSE_PROGRESS_VARIANTS = {
  task: {
    title: 'Closing task',
    done: 'Task closed',
    steps: [
      { percent: 14, message: 'Checking remaining work…' },
      { percent: 32, message: 'Saving time spent…' },
      { percent: 54, message: 'Marking this task complete…' },
      { percent: 76, message: 'Running follow-up actions…' },
      { percent: 92, message: 'Updating your board…' },
    ],
  },
  workflow: {
    title: 'Closing workflow',
    done: 'Workflow closed',
    steps: [
      { percent: 12, message: 'Checking linked tasks…' },
      { percent: 30, message: 'Saving time spent…' },
      { percent: 52, message: 'Closing this workflow…' },
      { percent: 74, message: 'Running follow-up actions…' },
      { percent: 90, message: 'Refreshing your board…' },
    ],
  },
  'force-workflow': {
    title: 'Force closing workflow',
    done: 'Workflow closed',
    steps: [
      { percent: 10, message: 'Completing linked tasks…' },
      { percent: 28, message: 'Completing subtasks…' },
      { percent: 48, message: 'Closing this workflow…' },
      { percent: 70, message: 'Running follow-up actions…' },
      { percent: 88, message: 'Saving changes…' },
    ],
  },
};

export const useStagedCloseProgress = (active, variant = 'task') => {
  const spec = CLOSE_PROGRESS_VARIANTS[variant] || CLOSE_PROGRESS_VARIANTS.task;
  const [visible, setVisible] = useState(false);
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState('');
  const wasActive = useRef(false);

  useEffect(() => {
    const current = CLOSE_PROGRESS_VARIANTS[variant] || CLOSE_PROGRESS_VARIANTS.task;
    if (active) {
      wasActive.current = true;
      setVisible(true);
      let index = 0;
      setPercent(current.steps[0].percent);
      setMessage(current.steps[0].message);
      const timer = window.setInterval(() => {
        index = Math.min(index + 1, current.steps.length - 1);
        setPercent(current.steps[index].percent);
        setMessage(current.steps[index].message);
        if (index >= current.steps.length - 1) {
          window.clearInterval(timer);
        }
      }, 420);
      return () => window.clearInterval(timer);
    }

    if (wasActive.current) {
      wasActive.current = false;
      setPercent(100);
      setMessage(current.done);
      const hide = window.setTimeout(() => {
        setVisible(false);
        setPercent(0);
      }, 480);
      return () => window.clearTimeout(hide);
    }

    setVisible(false);
    return undefined;
  }, [active, variant]);

  return { visible, percent, message, title: spec.title };
};

const WorkCloseProgressOverlay = ({ open, variant = 'task' }) => {
  const { visible, percent, message, title } = useStagedCloseProgress(open, variant);
  if (!visible) return null;

  return (
    <div
      className="absolute inset-0 z-[40] flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-[2px] px-6"
      role="status"
      aria-live="polite"
      aria-label={`${title}, ${percent} percent`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl px-5 py-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
          <p className="text-sm font-bold tabular-nums text-primary-700 dark:text-primary-300">{percent}%</p>
        </div>
        <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-primary-500 transition-[width] duration-300 ease-out"
            style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
          />
        </div>
        <p className="mt-2.5 text-[13px] text-gray-600 dark:text-gray-300">{message}</p>
      </div>
    </div>
  );
};

export default WorkCloseProgressOverlay;
